// window.atlas.preferences.* — the browser wrapper over the preferences API (Slice 7).
//
// Fills in the current app automatically (apps usually read/write their own preferences), so app
// code calls e.g. atlas.preferences.getPreferences({ preferenceName: "hide-expired" }). The user is
// taken from the auth token server-side. Mirrors the spec's window.harmony.preferences.* contract.

import type { ApiRequester } from "./api";
import type { PreferencesApi } from "./types";

export function makePreferences(api: ApiRequester, currentAppName: string): PreferencesApi {
  const appOf = (opts: { appName?: string }) => opts.appName || currentAppName;

  return {
    // Returns the parsed value (or null). { appName?, preferenceName, context? }
    async getPreferences({ appName, preferenceName, context } = {}) {
      requireField(preferenceName, "preferenceName");
      const query: Record<string, string> = { appName: appOf({ appName }), preferenceName: preferenceName! };
      if (context) query.context = context;
      const res = await api.request("GET", "/preferences", { query });
      return res.value;
    },

    // Writes a value; an empty/null value deletes it. { appName?, preferenceName, value, context? }
    async writePreferences({ appName, preferenceName, value, context } = {}) {
      requireField(preferenceName, "preferenceName");
      await api.request("POST", "/preferences", {
        body: { appName: appOf({ appName }), preferenceName, value, context },
      });
    },

    // Lists the CALLER's preferences for an app (paginated). { appName?, pageSize?, startToken? }
    // The user is always the authenticated caller; there is no cross-user scan.
    async scanPreferences({ appName, pageSize, startToken } = {}) {
      const query: Record<string, string> = { appName: appOf({ appName }) };
      if (pageSize) query.pageSize = String(pageSize);
      if (startToken) query.startToken = startToken;
      return api.request("GET", "/preferences/scan", { query });
    },
  };
}

function requireField(value: unknown, name: string): void {
  if (!value) throw new Error(`${name} is required`);
}
